import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';

const Sections = ({ 
    communityId, 
    uiLanguage,
    t
}) => {
    const sectionPageSize = 3;
    
    const [sections, setSections] = useState([]);
    const [sectionsState, setSectionsState] = useState({ page: 1, pages: 0 });
    const [loadingSections, setLoadingSections] = useState(true);
    const [sectionsError, setSectionsError] = useState(false);

    const competitionApiBase = `/${uiLanguage}/api/competition`;

    let pendingLoads = 0;
    const setGlobalLoader = (visible) => {
        if (visible) {
            pendingLoads += 1;
            if (typeof window.showLoader === 'function') {
                window.showLoader();
            }
            return;
        }
        pendingLoads = Math.max(0, pendingLoads - 1);
        if (pendingLoads === 0 && typeof window.hideLoader === 'function') {
            window.hideLoader();
        }
    };

    const loadSections = async (page, shouldScroll) => {
        setLoadingSections(true);
        setGlobalLoader(true);
        setSectionsError(false);
        try {
            const endpoint = `${competitionApiBase}/community/${communityId}/sections?page=${page}&pageSize=${sectionPageSize}`;
            const response = await fetch(endpoint, {
                headers: { 'Accept': 'application/json' }
            });
            if (!response.ok) throw new Error();
            const payload = await response.json();
            setSections(payload.items || []);
            setSectionsState({ page: payload.page || 1, pages: payload.pages || 0 });
            if (shouldScroll && window.matchMedia('(max-width: 768px)').matches) {
                document.getElementById('promo-sections-root')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } catch {
            setSectionsError(true);
        } finally {
            setLoadingSections(false);
            setGlobalLoader(false);
        }
    };

    useEffect(() => {
        loadSections(1, false);
    }, []);

    const PagerButtons = ({ state, onPage, prevLabel, nextLabel, pageLabel, ofLabel }) => {
        const hasPages = state.pages > 1;
        if (!hasPages) return null;
        
        return (
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', padding: '0 16px' }}>
                <button 
                    type="button" 
                    className="button promo-nav-btn" 
                    aria-label={prevLabel}
                    disabled={state.page <= 1}
                    onClick={() => onPage(state.page - 1, true)}
                >&lt;</button>
                <div className="promo-page-indicator translucid" style={{ textAlign: 'center', opacity: 0.85, color: '#fff' }}>
                    {pageLabel} {state.page} {ofLabel} {state.pages}
                </div>
                <button 
                    type="button" 
                    className="button promo-nav-btn" 
                    aria-label={nextLabel}
                    disabled={state.page >= state.pages}
                    onClick={() => onPage(state.page + 1, true)}
                >&gt;</button>
            </div>
        );
    };

    const isInitialLoadingSections = loadingSections && sections.length === 0 && !sectionsError;

    if (isInitialLoadingSections) {
        return null;
    }

    if (sectionsError) {
        return (
            <div id="promo-sections-root" className="center">
                <div className="promo-list-nav animated slide">
                    <div className="card left_text" style={{ gridArea: 'list' }}>{t.sectionsError}</div>
                </div>
            </div>
        );
    }

    if (!loadingSections && sections.length === 0) {
        return (
            <div id="promo-sections-root" className="center">
                <div className="promo-list-nav animated slide">
                    <div className="card left_text" style={{ gridArea: 'list' }}>{t.noSections}</div>
                </div>
            </div>
        );
    }

    return (
        <div id="promo-sections-root" className="center">
            <div className="promo-list-nav">
                <div className="container grid grid-row-order" style={{ marginTop: '20px', gridArea: 'list', gridColumn: '1 / -1' }}>
                    {sections.map(item => (
                        <div className="column" key={item.id}>
                            <div className="card left_text child-card animated slide">
                                <a href={item.url} className="title">{item.title}</a>
                                <a href={item.url} rel="nofollow">
                                    <img src={`/community/${communityId}/${item.image}`} alt={item.title} loading="lazy" className="animated cinematic" />
                                </a>
                                <p><i>{item.description}</i></p>
                            </div>
                        </div>
                    ))}
                </div>
                
                <PagerButtons 
                    state={sectionsState} 
                    onPage={loadSections} 
                    prevLabel={t.previous} 
                    nextLabel={t.next}
                    pageLabel={t.page}
                    ofLabel={t.of}
                />
            </div>
        </div>
    );
};

const initSections = () => {
    const rootEl = document.getElementById('sections-root');
    if (!rootEl) return;
    
    const communityId = Number(rootEl.dataset.communityId);
    const uiLanguage = (document.documentElement.lang || 'es').toLowerCase();
    
    const t = window.promoTranslations || {};
    
    const root = createRoot(rootEl);
    root.render(
        <Sections 
            communityId={communityId} 
            uiLanguage={uiLanguage}
            t={t}
        />
    );
};

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSections);
} else {
    initSections();
}

export default Sections;
